/**
 * ч.1361 — «Налаштування» відео-інструкцій → оформлення ролика: заставка з назвою ролика,
 * заклик підписатися всередині ролика, кінцева заставка.
 *
 * Файли оформлення (фон заставки, відео заклику, відео кінцевої заставки) — стандартні в коді;
 * редактор замінює їх тут, і завантажений файл діє одразу для наступних зйомок (окремо від
 * кнопки «зберегти», яка зберігає решту налаштувань). Назву на заставці пише система —
 * «переглянути заставку» показує кадр тією самою функцією, що й у ролику.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  platformVideo,
  type BrandingInfo,
  type BrandingKind,
  type VideoSettings,
} from '@/api/platformVideo';
import { extractApiError } from '@/utils/extractApiError';

export function NumField({ id, label, value, onChange, min, max, step, hint, span = 3 }: {
  id: string; label: string; value: number; onChange: (v: number) => void;
  /** 'any' — для дробових значень не на сітці кроку (збільшення 1,3333): інакше браузер не
   *  відправить форму («введіть допустиме значення»). */
  min: number; max: number; step: number | 'any'; hint?: string; span?: number;
}) {
  return (
    <div className="field" style={{ gridColumn: `span ${span}` }}>
      <label htmlFor={id}>{label}</label>
      <input id={id} type="number" value={value} min={min} max={max} step={step}
        onChange={(e) => onChange(Number(e.target.value))} />
      {hint && <div className="cell-hint" title={hint}>{hint}</div>}
    </div>
  );
}

const HINT = { fontSize: '0.85rem', color: 'var(--text-secondary)' } as const;

/** Звільнити посилання на blob (у старих браузерах і тестовому середовищі функції може не бути). */
const revoke = (u: string) => { try { URL.revokeObjectURL?.(u); } catch { /* не критично */ } };

const fmtSize = (b: number) => (b >= 1024 * 1024
  ? `${(b / (1024 * 1024)).toLocaleString('uk-UA', { maximumFractionDigits: 1 })} МБ`
  : `${Math.max(1, Math.round(b / 1024)).toLocaleString('uk-UA')} КБ`);

/** Рядок файлу оформлення: що зараз стоїть, переглянути, завантажити свій, повернути стандартний. */
export function BrandingFileRow({ info, hint }: { info: BrandingInfo | undefined; hint: string }) {
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  useEffect(() => () => { if (url) revoke(url); }, [url]);
  const closePreview = () => setUrl(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['platform-video-settings'] });
  const preview = useMutation({
    mutationFn: () => platformVideo.brandingFileUrl(info!.kind),
    onSuccess: (u) => { setErr(null); setUrl(u); },
    onError: (e) => setErr(extractApiError(e)),
  });
  const upload = useMutation({
    mutationFn: (f: File) => platformVideo.uploadBranding(info!.kind, f),
    onSuccess: () => {
      setErr(null); closePreview(); refresh();
      setDone('Файл збережено — він піде в наступні зйомки (готові ролики — після «перезняти»).');
    },
    onError: (e) => { setDone(null); setErr(extractApiError(e)); },
  });
  const reset = useMutation({
    mutationFn: () => platformVideo.resetBranding(info!.kind),
    onSuccess: () => { setErr(null); closePreview(); refresh(); setDone('Повернуто стандартний файл.'); },
    onError: (e) => { setDone(null); setErr(extractApiError(e)); },
  });
  if (!info) return null;
  const busy = upload.isPending || reset.isPending;
  const state = !info.present
    ? 'файлу немає'
    : info.custom
      ? `завантажений ${info.updated_at ? new Date(info.updated_at).toLocaleDateString('uk-UA') : ''}, ${fmtSize(info.size)}`
      : `стандартний, ${fmtSize(info.size)}`;
  return (
    <div data-testid={`branding-${info.kind}`} style={{ margin: '0.25rem 0 0.75rem' }}>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <strong style={{ fontWeight: 600 }}>{info.label}:</strong>
        <span style={HINT}>{state}</span>
        {url ? (
          <button type="button" onClick={closePreview}>сховати</button>
        ) : (
          <button type="button" onClick={() => preview.mutate()} disabled={!info.present || preview.isPending}>
            {preview.isPending ? 'відкриваємо…' : 'переглянути'}
          </button>
        )}
        <button type="button" onClick={() => input.current?.click()} disabled={busy}>
          {upload.isPending ? 'завантажуємо…' : 'завантажити інший'}
        </button>
        <button type="button" onClick={() => reset.mutate()} disabled={busy || !info.custom}
          title={info.custom ? undefined : 'Зараз і так стоїть стандартний файл'}>
          повернути стандартний
        </button>
        <input ref={input} type="file" accept={info.accept} hidden aria-label={`${info.label}: файл`}
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) upload.mutate(f);
          }} />
      </div>
      <div style={HINT}>{hint}</div>
      {done && <div role="status" style={{ ...HINT, color: 'var(--badge-green-fg)' }}>{done}</div>}
      {err && <div className="error">{err}</div>}
      {url && (info.media === 'image'
        ? <img src={url} alt={info.label} style={{ display: 'block', maxWidth: 640, width: '100%', marginTop: '0.5rem', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }} />
        : <video src={url} controls style={{ display: 'block', maxWidth: 640, width: '100%', marginTop: '0.5rem', borderRadius: 'var(--radius)' }} />)}
    </div>
  );
}

type Setter = <K extends keyof VideoSettings>(k: K, v: VideoSettings[K]) => void;

export default function VideoBrandingSection({ form, set, branding, canListen }: {
  form: VideoSettings;
  set: Setter;
  branding: Partial<Record<BrandingKind, BrandingInfo>> | undefined;
  /** «Прослухати» текст заклику — лише голосом ElevenLabs (Piper працює в робітнику). */
  canListen: boolean;
}) {
  const [sampleTitle, setSampleTitle] = useState('Налаштування організації');
  const [introUrl, setIntroUrl] = useState<string | null>(null);
  useEffect(() => () => { if (introUrl) revoke(introUrl); }, [introUrl]);
  const intro = useMutation({
    mutationFn: () => platformVideo.introPreview(sampleTitle.trim() || 'Назва ролика', form),
    onSuccess: (u) => setIntroUrl(u),
  });
  const listen = useMutation({
    mutationFn: async () => {
      const url = await platformVideo.sample(form.voice, form.elevenlabs_model, form.speech_rate, form.promo_text.trim());
      const audio = new Audio(url);
      audio.addEventListener('ended', () => revoke(url));
      try {
        await Promise.resolve(audio.play());
      } catch { /* браузер заборонив автовідтворення — звук не критичний */ }
    },
  });

  return (
    <>
      <h3>Заставка на початку</h3>
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label htmlFor="vs-intro-style">Вигляд заставки</label>
          <select id="vs-intro-style" value={form.intro_style}
            onChange={(e) => set('intro_style', e.target.value as VideoSettings['intro_style'])}>
            <option value="image">картинка з назвою ролика</option>
            <option value="card">кольорове тло з текстом</option>
          </select>
        </div>
        <NumField id="vs-intro-s" label="Тривалість, с" value={form.intro_seconds} min={0} max={8} step={0.5}
          onChange={(v) => set('intro_seconds', v)} hint="0 — без заставки" />
        {form.intro_style === 'card' ? (
          <div className="field" style={{ gridColumn: 'span 5' }}>
            <label htmlFor="vs-intro">Початок назви на заставці</label>
            <input id="vs-intro" value={form.intro_title_prefix} onChange={(e) => set('intro_title_prefix', e.target.value)} />
          </div>
        ) : <div className="field" style={{ gridColumn: 'span 5' }} />}
      </div>
      {form.intro_style === 'image' && (
        <>
          <BrandingFileRow info={branding?.intro_bg}
            hint="Картинка 16:9 (найкраще 1920×1080) без назви — назву ролика система пише сама в блоці нижче. Діє одразу для наступних зйомок." />
          <div className="doc-grid">
            <div className="field" style={{ gridColumn: 'span 3' }}>
              <label htmlFor="vs-title-color">Колір назви</label>
              <input id="vs-title-color" type="color" value={form.intro_title_color}
                onChange={(e) => set('intro_title_color', e.target.value)} style={{ height: '2.2rem', padding: 2 }} />
            </div>
            <NumField id="vs-title-size" label="Найбільший розмір літер, px" value={form.intro_title_size}
              min={40} max={200} step={2} onChange={(v) => set('intro_title_size', v)}
              hint="Довга назва зменшується сама, до 4 рядків" />
            <div className="field" style={{ gridColumn: 'span 6' }} />
          </div>
          <div className="doc-grid">
            <NumField id="vs-title-x" label="Блок назви: відступ зліва" value={form.intro_title_x} min={0} max={1800} step={1}
              onChange={(v) => set('intro_title_x', v)} hint="Пікселі кадру 1920×1080" />
            <NumField id="vs-title-y" label="Відступ згори" value={form.intro_title_y} min={0} max={1000} step={1}
              onChange={(v) => set('intro_title_y', v)} hint="До верху великих літер" />
            <NumField id="vs-title-w" label="Ширина" value={form.intro_title_w} min={200} max={1900} step={1}
              onChange={(v) => set('intro_title_w', v)} />
            <NumField id="vs-title-h" label="Висота" value={form.intro_title_h} min={60} max={1000} step={1}
              onChange={(v) => set('intro_title_h', v)} />
          </div>
          <div className="doc-grid">
            <div className="field" style={{ gridColumn: 'span 8' }}>
              <label htmlFor="vs-sample-title">Назва для перегляду</label>
              <input id="vs-sample-title" value={sampleTitle} maxLength={200} onChange={(e) => setSampleTitle(e.target.value)} />
            </div>
            <div className="field" style={{ gridColumn: 'span 4', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => intro.mutate()} disabled={intro.isPending}>
                {intro.isPending ? 'малюємо…' : 'переглянути заставку'}
              </button>
            </div>
          </div>
          {intro.error && <div className="error">{extractApiError(intro.error)}</div>}
          {introUrl && (
            <img src={introUrl} alt="Заставка ролика" data-testid="intro-preview"
              style={{ display: 'block', maxWidth: 640, width: '100%', margin: '0.25rem 0 0.5rem', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }} />
          )}
        </>
      )}

      <h3>Заклик підписатися</h3>
      <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
        <input type="checkbox" checked={form.promo_enabled} style={{ width: 'auto', margin: 0 }}
          onChange={(e) => set('promo_enabled', e.target.checked)} />
        вставляти в ролик коротке відео із закликом підписатися на канал
      </label>
      {form.promo_enabled && (
        <>
          <BrandingFileRow info={branding?.promo}
            hint="Відео може бути без звуку — озвучку система накладає текстом нижче тим самим голосом, що й ролик. Якщо фраза довша за відео, останній кадр тримається до її кінця." />
          <div className="doc-grid">
            <div className="field" style={{ gridColumn: 'span 9' }}>
              <label htmlFor="vs-promo-text">Текст озвучки заклику</label>
              <textarea id="vs-promo-text" rows={2} maxLength={400} value={form.promo_text}
                onChange={(e) => set('promo_text', e.target.value)} />
              <div className="cell-hint">Порожній текст — заклик без озвучки</div>
            </div>
            <div className="field" style={{ gridColumn: 'span 3', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => listen.mutate()}
                disabled={!canListen || listen.isPending || !form.promo_text.trim()}
                title={canListen ? 'Цей текст обраним голосом ElevenLabs (повторне прослуховування символів не витрачає)'
                  : 'Прослухати можна з голосом ElevenLabs; голос Piper звучить у готовому ролику'}>
                {listen.isPending ? 'озвучуємо…' : 'прослухати заклик'}
              </button>
            </div>
          </div>
          {listen.error && <div className="error">{extractApiError(listen.error)}</div>}
          <div className="doc-grid">
            <NumField id="vs-promo-at" label="Місце в ролику, %" value={form.promo_at_percent} min={10} max={90} step={5}
              onChange={(v) => set('promo_at_percent', v)} hint="Заклик стає в найближчу паузу між кроками" />
            <NumField id="vs-promo-min" label="Лише в роликах від, с" value={form.promo_min_video_seconds} min={0} max={1800} step={5}
              onChange={(v) => set('promo_min_video_seconds', v)} hint="Коротші ролики — без заклику" />
            <NumField id="vs-promo-delay" label="Озвучка починається через, с" value={form.promo_voice_delay} min={0} max={5} step={0.1}
              onChange={(v) => set('promo_voice_delay', v)} />
            <div className="field" style={{ gridColumn: 'span 3' }} />
          </div>
        </>
      )}

      <h3>Кінцева заставка</h3>
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label htmlFor="vs-outro-style">Вигляд кінцевої заставки</label>
          <select id="vs-outro-style" value={form.outro_style}
            onChange={(e) => set('outro_style', e.target.value as VideoSettings['outro_style'])}>
            <option value="clip">відео</option>
            <option value="card">кольорове тло з текстом</option>
            <option value="none">без кінцевої заставки</option>
          </select>
        </div>
        {form.outro_style === 'card' ? (
          <>
            <div className="field" style={{ gridColumn: 'span 5' }}>
              <label htmlFor="vs-outro">Текст наприкінці</label>
              <input id="vs-outro" value={form.outro_text} onChange={(e) => set('outro_text', e.target.value)} />
            </div>
            <NumField id="vs-outro-s" label="Тривалість, с" value={form.outro_seconds} min={0} max={8} step={0.5}
              onChange={(v) => set('outro_seconds', v)} />
          </>
        ) : <div className="field" style={{ gridColumn: 'span 8' }} />}
      </div>
      {form.outro_style === 'clip' && (
        <BrandingFileRow info={branding?.outro}
          hint="Відео зі звуком або без; музику система вирівнює, щоб вона звучала тихіше за голос. Ролик закінчується разом із цим відео." />
      )}
    </>
  );
}
